import { useEffect, useRef, useState } from "react"
import { useBlockAutoReload } from "@/lib/standalone-recovery"

// Debounced autosave for editor forms.
//
// Watches `value`. The first mount snapshots whatever's passed in but
// does NOT fire `save` — that's the freshly-loaded prop state, nothing
// to save. After that, any change to `value` schedules `save()` to run
// `delay`ms after the last change. Consecutive changes within the
// window collapse into a single save. No-op when `value` matches the
// last-saved snapshot, so re-renders from prop updates after a save
// (which reset local state on full Inertia visits) don't loop.
//
// `save` is always called via a ref, so callers don't need to memoize
// it — the latest closure is used at fire time. Pair with
// `preserveState: true` on the underlying router call so the user's
// in-progress typing isn't trashed by the redirect response.
//
// `onSaved` fires alongside the save call, so callers can pulse a
// "Saved" indicator at the same moment the PATCH goes out.
export function useAutosave<T>(
  value: T,
  save: () => void,
  opts: { delay?: number; enabled?: boolean; onSaved?: () => void } = {},
): void {
  const { delay = 600, enabled = true, onSaved } = opts

  const isFirst    = useRef(true)
  const lastSaved  = useRef<string>("")
  const saveRef    = useRef(save)
  const onSavedRef = useRef(onSaved)
  saveRef.current    = save
  onSavedRef.current = onSaved

  // Dirty = there's an edit pending that hasn't been flushed to the server
  // yet. Held from the first change until the debounced save fires. While
  // dirty we block the standalone recovery guard from reloading the page —
  // a reload mid-edit would destroy the user's unsaved typing.
  const [dirty, setDirty] = useState(false)
  useBlockAutoReload(dirty)

  useEffect(() => {
    const serialized = JSON.stringify(value)

    if (isFirst.current) {
      isFirst.current = false
      lastSaved.current = serialized
      return
    }

    if (!enabled) return
    if (serialized === lastSaved.current) return

    setDirty(true)
    const timeout = window.setTimeout(() => {
      lastSaved.current = serialized
      saveRef.current()
      onSavedRef.current?.()
      setDirty(false)
    }, delay)

    return () => window.clearTimeout(timeout)
  }, [value, delay, enabled])
}

// Per-field "just saved" indicator. Track which field was most recently
// edited via a ref (no re-renders on each keystroke), then on save
// surface that key in state for `clearAfterMs` so the bound field can
// render a check icon and fade it out.
export function useFieldSaveIndicator(clearAfterMs = 2000): {
  markEdited: (key: string) => void
  markSaved: () => void
  savedKey: string | null
} {
  const lastEditedRef = useRef<string | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)

  useEffect(() => {
    if (savedKey == null) return
    const t = window.setTimeout(() => setSavedKey(null), clearAfterMs)
    return () => window.clearTimeout(t)
  }, [savedKey, clearAfterMs])

  return {
    markEdited: (key: string) => {
      lastEditedRef.current = key
    },
    markSaved: () => {
      const k = lastEditedRef.current
      if (k != null) {
        // Re-trigger the effect even if savedKey already equals `k` so
        // the 2s clear timer restarts on a back-to-back save.
        setSavedKey(null)
        // Defer to next tick so React sees two distinct state updates.
        window.queueMicrotask(() => setSavedKey(k))
      }
    },
    savedKey,
  }
}
